import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Card, Table, Form, Button, Badge, Modal, Row, Col, Spinner, InputGroup, Alert } from 'react-bootstrap';
import { FaEye, FaTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import { FaUsers, FaUserShield, FaHeadset } from "react-icons/fa";
import api from '../../service/api';
import AssignmentAreaSelects, { telecallerAreaDisplay } from '../../components/AssignmentAreaSelects';
import { useToast } from '../../contexts/ToastContext';
import './UserManagement.css';


const ROLE_LABELS = {
  admin: 'Admin',
  telecaller: 'Telecaller',
};

const emptyForm = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  role: 'telecaller',
  assignedWard: '',
  assignedWardNo: '',
  assignedBooth: ''
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, admins: 0, telecallers: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  useEffect(() => {
    fetchUsers();

  }, [currentPage, search, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/users', {
        params: { page: currentPage, limit: 5, search, status: statusFilter },
      });
      setUsers(res.data.users);
      setTotalPages(res.data.pagination.pages);
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (error) {
      showError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/api/admin/users/${userId}`, { isActive: !currentStatus });

      showSuccess('User status updated successfully');
      fetchUsers();
    } catch (error) {
      const serverMsg = error.response?.data?.message || error.message || 'Unknown error';
      showError(`Failed to update user status: ${serverMsg}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);

      showSuccess('User deleted successfully');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to delete user';
      showError(msg);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const openModal = () => {
    setFormData(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setFormLoading(true);
    setFormError('');
    try {
      await api.post('/api/admin/users', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        assignedWard: formData.assignedWard,
        assignedWardNo: formData.assignedWardNo,
        assignedBooth: formData.assignedBooth,
        profile: {
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
      });
      closeModal();
      showSuccess(`User "${formData.username}" created successfully`);
      fetchUsers();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Failed to create user.');
    } finally {
      setFormLoading(false);
    }
  };


  const getInitials = (user) => {
    if (user.profile?.firstName && user.profile?.lastName) {
      return (user.profile.firstName[0] + user.profile.lastName[0]).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-vh-100 p-3 p-md-3 bg-light um-page">
      <Container fluid className="mx-auto um-content">

        <div className="um-header">
          <div className="um-header-copy">
            <h1 className="um-header-title">User Management</h1>
            <p className="um-header-subtitle">
              Secure control of staff and administrative accounts
            </p>
          </div>

          <Button
            onClick={openModal}
            className="um-create-btn shadow-sm rounded-2"
          >
            <FaPlus aria-hidden="true" />
            Create New User
          </Button>
        </div>

        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create New User</Modal.Title>
          </Modal.Header>

          <Form onSubmit={handleCreateUser}>
            <Modal.Body>

              {formError && (
                <Alert variant="danger" className="py-2 small">
                  {formError}
                </Alert>
              )}

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">First Name</Form.Label>
                    <Form.Control
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleFormChange}
                      placeholder="Enter first name"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Last Name</Form.Label>
                    <Form.Control
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleFormChange}
                      placeholder="Enter last name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Username *</Form.Label>
                <Form.Control
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  required
                  placeholder="Enter username"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  placeholder="Enter email"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Confirm Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleFormChange}
                  required
                  minLength={6}
                  placeholder="Confirm password"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Role *</Form.Label>
                <Form.Select
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                >
                  <option value="telecaller">Telecaller</option>
                  <option value="admin">Admin</option>
                </Form.Select>
              </Form.Group>

            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>

              <Button type="submit" disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create User'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>


        <Row className="um-stats">

          <Col md={4}>
            <Card className="um-stat-card">
              <div className="um-stat-icon bg-primary bg-opacity-10 text-primary">
                <FaUsers />
              </div>
              <div>
                <div className="um-stat-label">Total Staff</div>
                <div className="um-stat-value">{stats.total}</div>
              </div>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="um-stat-card">
              <div className="um-stat-icon bg-secondary bg-opacity-10 text-dark">
                <FaUserShield />
              </div>
              <div>
                <div className="um-stat-label">Active Admins</div>
                <div className="um-stat-value">{stats.admins}</div>
              </div>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="um-stat-card">
              <div className="um-stat-icon bg-success bg-opacity-10 text-success">
                <FaHeadset />
              </div>
              <div>
                <div className="um-stat-label">Active Telecallers</div>
                <div className="um-stat-value">{stats.telecallers}</div>
              </div>
            </Card>
          </Col>

        </Row>


        <Card className="um-table-card border-0 shadow-sm overflow-hidden">
          <Card.Header className="um-table-toolbar bg-white border-bottom">
            <InputGroup className="um-search bg-light rounded-3 overflow-hidden border">
              <InputGroup.Text className="bg-light border-0 text-secondary py-0">
                <FaSearch aria-hidden="true" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search operators..."
                aria-label="Search operators"
                className="um-search-input bg-light border-0 shadow-none"
                value={search}
                onChange={handleSearch}
              />
            </InputGroup>

            <Form.Select 
              className="um-status-filter bg-light border shadow-none rounded-3" 
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="suspended">Suspended Only</option>
            </Form.Select>
          </Card.Header>

          <div className="user-table-scroll um-table-scroll">
            <Table borderless hover className="mb-0 custom-enterprise-table user-table um-users-table">

              <thead className="bg-primary bg-opacity-10">
                <tr>
                  {['USER / NAME', 'EMAIL ADDRESS', 'SYSTEM ROLE', 'ASSEMBLY', 'WARD', 'BOOTH', 'STATUS', 'JOINED', 'ACTIONS'].map((h, i) => (
                    <th key={i}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {users.map((u) => {

                  const isSelf = currentUser?._id === u._id;
                  const area = telecallerAreaDisplay(u);

                  return (
                    <tr key={u._id} className={isSelf ? "bg-primary bg-opacity-10" : "hover-row"}>

                      <td>
                        <div className="um-user-cell">
                          <div className="um-user-avatar bg-primary bg-opacity-10 text-primary">
                            {getInitials(u)}
                          </div>
                          <div className="um-user-copy">
                            <div className="um-user-heading">
                              <span className="um-user-name">{u.username}</span>
                              {isSelf && <Badge pill bg="primary" className="um-you-badge">YOU</Badge>}
                            </div>
                            <div className="um-user-sub">
                              {u.profile?.firstName} {u.profile?.lastName}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="um-muted">{u.email}</td>

                      <td>
                        <span className={`um-role-badge badge ${u.role === 'admin' ? 'bg-primary' : 'bg-secondary'}`}>
                          {u.role}
                        </span>
                      </td>

                      <td className="um-muted">{area.assembly}</td>
                      <td className="um-muted">{area.ward}</td>
                      <td className="um-muted">{area.booth}</td>

                      <td>
                        <button 
                          className={`um-status-pill btn rounded-pill btn-sm ${u.isActive ? 'btn-success' : 'btn-danger'}`}
                          onClick={() => handleToggleStatus(u._id, u.isActive)}
                        >
                          <span className="um-status-dot" aria-hidden="true" />
                          {u.isActive ? 'Active' : 'Suspended'}
                        </button>
                      </td>

                      <td className="um-muted">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>

                      <td>
                        <div className="um-actions">
                          <Link
                            to={`/admin/users/${u._id}`}
                            className="um-action-btn btn btn-outline-primary rounded-circle"
                            title="View Profile"
                          >
                            <FaEye />
                          </Link>

                          <Button
                            className={`um-action-btn btn rounded-circle bg-body ${u.role === 'admin' ? 'btn-outline-secondary opacity-50' : 'btn-outline-danger'}`}
                            onClick={() => handleDeleteUser(u._id)}
                            title={u.role === 'admin' ? "Cannot delete Admin accounts" : "Delete Operator"}
                            disabled={u.role === 'admin'}
                          >
                            <FaTrashAlt />
                          </Button>
                        </div>
                      </td>

                    </tr>
                  )
                })}
              </tbody>

            </Table>
          </div>

            {totalPages > 1 && (
              <Card.Footer className="um-table-footer bg-white border-0 d-flex justify-content-center">

                <div className="d-flex align-items-center gap-2">


                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`btn btn-sm ${currentPage === 1 ? 'btn-light text-muted' : 'btn-outline-primary'
                      }`}
                  >
                    Prev
                  </button>


                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    const isActive = page === currentPage;

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`btn btn-sm ${isActive ? 'btn-success text-white' : 'btn-outline-secondary'
                          }`}
                        style={{ minWidth: "32px" }}
                      >
                        {page}
                      </button>
                    );
                  })}


                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`btn btn-sm ${currentPage === totalPages ? 'btn-light text-muted' : 'btn-primary'
                      }`}
                  >
                    Next
                  </button>

                </div>

              </Card.Footer>
            )}
        </Card>

      </Container>

      
      <Modal show={showModal} onHide={closeModal} centered size="lg" contentClassName="border-0 shadow-lg rounded-4">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <div>
            <Modal.Title className="fw-bold text-dark h5 mb-1">Create user</Modal.Title>
            <p className="text-secondary small mb-0">Add a telecaller or admin and set their call area.</p>
          </div>
        </Modal.Header>
        <Form onSubmit={handleCreateUser}>
          <Modal.Body className="px-4 py-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {formError && <Alert variant="danger" className="py-2 small mb-3">{formError}</Alert>}

            <div className="text-secondary small fw-bold text-uppercase ls-wider mb-2">Profile</div>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">First name</Form.Label>
                  <Form.Control
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleFormChange}
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="First name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Last name</Form.Label>
                  <Form.Control
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleFormChange}
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="Last name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="text-secondary small fw-bold text-uppercase ls-wider mb-2">Account</div>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Username *</Form.Label>
                  <Form.Control
                    name="username"
                    value={formData.username}
                    onChange={handleFormChange}
                    required
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="jdoe_01"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="jdoe@telicaller.io"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Password *</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    required
                    minLength={6}
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="Minimum 6 characters"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Confirm password *</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleFormChange}
                    required
                    minLength={6}
                    className="bg-white border shadow-none rounded-3 py-2"
                    placeholder="Re-enter password"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-slate-500 mb-1">Role *</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className="bg-white border shadow-none rounded-3 py-2"
                  >
                    <option value="telecaller">Telecaller</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {formData.role === 'telecaller' && (
              <AssignmentAreaSelects
                value={formData}
                onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
              />
            )}
          </Modal.Body>
          <Modal.Footer className="border-top px-4 py-3 bg-white rounded-bottom-4">
            <Button variant="light" onClick={closeModal} className="px-4 rounded-3">Cancel</Button>
            <Button
              type="submit"
              disabled={formLoading}
              className="px-4 border-0 shadow-sm rounded-3"
              style={{ backgroundColor: '#2563eb' }}
            >
              {formLoading ? 'Creating...' : 'Create user'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      
    </div>
  );
};

export default UserManagement;
